import React, { useCallback, useMemo } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { AnchoredMenu } from '@rossum/components/anchored-menu/AnchoredMenu';
import { SelectionPanelActionDescriptor } from '@rossum/components/selection-panel/createSelectionPanelItem';
import { SelectionPanel as BaseSelectionPanel } from '@rossum/components/selection-panel/SelectionPanel';
import { IconClipboard, IconEdit, IconTrash } from '@rossum/ui/icons/tabler';
import { Menu, MenuItem, SvgIcon, Typography } from '@rossum/ui/material';
import {
  GridApi,
  gridExpandedSortedRowIdsSelector,
  GridRowId,
  GridRowSelectionModel,
  gridVisibleColumnDefinitionsSelector,
} from '@rossum/ui/x-data-grid-pro';
import { GridColumns } from '../columns/types';
import { RemoveDialogParamsSetter } from '../hooks/useRemoveFromQueues';
import { GridRowModel, GridRowModelKeys } from '../rows/rowTypes';

type SelectionPanelProps = {
  visible: boolean;
  editableColumns: Array<GridColumns[number] & { isBatchEditable: boolean }>;
  // CHECK GridApiPro => GridApi
  gridApiRef: React.MutableRefObject<GridApi | null>;
  selectedRows: GridRowModel[];
  startBatchEditMode: (params: { id: GridRowId; field: string }) => void;
  setRowSelectionModel: React.Dispatch<
    React.SetStateAction<GridRowSelectionModel>
  >;
  openDeleteModal: RemoveDialogParamsSetter;
  shouldDisplayPasteOption: boolean;
  pasteFieldConfigurationToRows: (rows: GridRowModel[]) => void;
  setDataForBulkEditDrawer: React.Dispatch<
    React.SetStateAction<{
      field: Extract<GridRowModelKeys, 'options' | 'formula'>;
      aggregations?: Set<string>;
      rows?: GridRowModel[] | undefined;
    } | null>
  >;
};

export const SelectionPanel = ({
  visible,
  editableColumns,
  gridApiRef,
  selectedRows,
  startBatchEditMode,
  setRowSelectionModel,
  openDeleteModal,
  shouldDisplayPasteOption,
  pasteFieldConfigurationToRows,
  setDataForBulkEditDrawer,
}: SelectionPanelProps) => {
  const intl = useIntl();

  const handleDeselect = () => {
    setRowSelectionModel({ type: 'include', ids: new Set() });
  };

  const handleEditColumnValueClick = useCallback(
    (field: GridRowModelKeys) => {
      const firstRowId = gridApiRef.current
        ?.getSortedRowIds()
        .find(id => gridApiRef.current?.getSelectedRows().has(id));

      if (!firstRowId) {
        throw new Error('No row selected');
      }

      if (field === 'options' || field === 'formula') {
        setDataForBulkEditDrawer({
          field,
          aggregations: new Set([]),
          rows: selectedRows,
        });
      } else {
        // handle scroll to the cell in case it is not visible
        // due to virtualization the cell does not even need to be rendered and
        // scrolling to focused input handled by browser automatically will not work
        const rowIndex = gridExpandedSortedRowIdsSelector(gridApiRef).findIndex(
          id => id === firstRowId
        );
        const colIndex = gridVisibleColumnDefinitionsSelector(
          gridApiRef
        ).findIndex(column => column.field === field);
        gridApiRef.current?.scrollToIndexes({ rowIndex, colIndex });

        startBatchEditMode({ id: firstRowId, field });
      }
    },
    [gridApiRef, selectedRows, setDataForBulkEditDrawer, startBatchEditMode]
  );

  const selectedLabel = (
    <Typography variant="body2">
      {intl.formatMessage(
        {
          id: 'features.fieldManager.fieldDetail.selection.selectionPanel.selected.row',
        },
        { count: selectedRows.length }
      )}{' '}
      {intl.formatMessage({
        id: 'features.fieldManager.fieldDetail.selection.selectionPanel.selected',
      })}
    </Typography>
  );

  const items = useMemo(
    () =>
      R.pipe(
        [
          shouldDisplayPasteOption
            ? ({
                id: 'paste',
                label: intl.formatMessage({
                  id: 'features.fieldManager.fieldDetail.selection.selectionPanel.button.paste',
                }),
                icon: (
                  <SvgIcon>
                    <IconClipboard />
                  </SvgIcon>
                ),
                onClick: () => {
                  pasteFieldConfigurationToRows(selectedRows);
                  setRowSelectionModel({ type: 'include', ids: new Set() });
                },
                'data-cy': 'fm-batch-paste-field-config-from-clipboard',
              } satisfies SelectionPanelActionDescriptor)
            : null,
          {
            id: 'edit',
            label: intl.formatMessage({
              id: 'features.fieldManager.fieldDetail.selection.editValue.button.edit',
            }),
            icon: (
              <SvgIcon>
                <IconEdit />
              </SvgIcon>
            ),
            renderSubMenu: ({ renderTrigger, nested }) => (
              <AnchoredMenu
                nested={nested}
                renderContent={({
                  anchorEl,
                  open,
                  onClose,
                  anchorOrigin,
                  transformOrigin,
                }) => (
                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={onClose}
                    anchorOrigin={anchorOrigin}
                    transformOrigin={transformOrigin}
                    MenuListProps={{ dense: true }}
                    sx={{ maxHeight: 345 }}
                  >
                    {editableColumns.map(column => (
                      <MenuItem
                        disabled={!column.isBatchEditable}
                        key={column.field}
                        data-cy={`fm-edit-value-item-${column.field}`}
                        onClick={() => {
                          onClose();
                          handleEditColumnValueClick(
                            column.field as GridRowModelKeys
                          );
                        }}
                      >
                        {column.headerName}
                      </MenuItem>
                    ))}
                  </Menu>
                )}
              >
                {renderTrigger}
              </AnchoredMenu>
            ),
            'data-cy': 'fm-batch-edit-value-button',
          } satisfies SelectionPanelActionDescriptor,
          {
            id: 'delete',
            label: intl.formatMessage({
              id: 'features.fieldManager.fieldDetail.selection.selectionPanel.button.delete',
            }),
            icon: (
              <SvgIcon>
                <IconTrash />
              </SvgIcon>
            ),
            onClick: () => openDeleteModal({ selectedRows }),
            'data-cy': 'fm-batch-remove-field',
          } satisfies SelectionPanelActionDescriptor,
        ],
        R.filter(R.isTruthy)
      ),
    [
      editableColumns,
      handleEditColumnValueClick,
      intl,
      openDeleteModal,
      pasteFieldConfigurationToRows,
      selectedRows,
      setRowSelectionModel,
      shouldDisplayPasteOption,
    ]
  );

  return (
    <BaseSelectionPanel
      visible={visible}
      selectedLabel={selectedLabel}
      onDeselect={handleDeselect}
      actions={items}
      sx={{ mt: 2 }}
    />
  );
};
